/**
 * Menu Builder Layout Hook
 * 
 * React hook for managing menu builder layouts using TanStack Query.
 * Provides data fetching, mutations, and cache management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchMenuBuilderLayout, 
  fetchPublishedMenuLayout,
  saveMenuBuilderLayout, 
  publishMenuBuilderLayout,
  unpublishMenuBuilderLayout,
  deleteMenuBuilderLayout,
  MenuBuilderServiceError,
} from '@/services/menuBuilderService';
import { 
  MenuBuilderLayout, 
  MenuBuilderLayoutRecord,
} from '@/types/menuBuilder';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

/**
 * Query key factory for menu builder layouts
 */
export const menuBuilderKeys = {
  all: ['menu-builder-layouts'] as const,
  layout: (branchId: string) => [...menuBuilderKeys.all, 'layout', branchId] as const,
  published: (branchId: string) => [...menuBuilderKeys.all, 'published', branchId] as const,
};

/**
 * Hook for managing menu builder layout for a branch
 */
export function useMenuBuilderLayout(branchId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Fetch layout query
  const { 
    data: layoutRecord, 
    isLoading, 
    error,
    refetch,
  } = useQuery({
    queryKey: menuBuilderKeys.layout(branchId),
    queryFn: () => fetchMenuBuilderLayout(branchId),
    enabled: !!branchId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: ({ 
      layout, 
      expectedVersion,
      suppressToast
    }: { 
      layout: MenuBuilderLayout; 
      expectedVersion?: number;
      suppressToast?: boolean;
    }) => saveMenuBuilderLayout(branchId, layout, expectedVersion),
    
    onSuccess: (data, variables) => {
      queryClient.setQueryData(menuBuilderKeys.layout(branchId), data);
      
      if (!variables.suppressToast) {
        toast({
          title: t('menuBuilder.actions.savedSuccessfully', 'Layout saved'),
          description: t('menuBuilder.actions.savedSuccessfullyDesc', 'Your menu layout has been saved.'),
        });
      }
    },
    
    onError: (error: MenuBuilderServiceError) => {
      if (error.code === 'VERSION_CONFLICT') {
        toast({
          title: t('menuBuilder.errors.versionConflict', 'Version conflict'),
          description: t('menuBuilder.errors.versionConflictDesc', 'Someone else has modified this layout. Please refresh and try again.'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('menuBuilder.errors.saveFailed', 'Save failed'),
          description: error.message,
          variant: 'destructive',
        });
      }
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: () => publishMenuBuilderLayout(branchId),
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuBuilderKeys.layout(branchId) });
      queryClient.invalidateQueries({ queryKey: menuBuilderKeys.published(branchId) });
      toast({
        title: t('menuBuilder.actions.publishedSuccessfully', 'Menu published'),
        description: t('menuBuilder.actions.publishedSuccessfullyDesc', 'Your menu is now live for customers.'),
      });
    },
    
    onError: (error: MenuBuilderServiceError) => {
      toast({
        title: t('menuBuilder.errors.publishFailed', 'Publish failed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Unpublish mutation
  const unpublishMutation = useMutation({
    mutationFn: () => unpublishMenuBuilderLayout(branchId),
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuBuilderKeys.layout(branchId) });
      queryClient.invalidateQueries({ queryKey: menuBuilderKeys.published(branchId) });
      toast({
        title: t('menuBuilder.actions.unpublishedSuccessfully', 'Menu unpublished'),
        description: t('menuBuilder.actions.unpublishedSuccessfullyDesc', 'Customers will now see the default menu.'),
      });
    },
    
    onError: (error: MenuBuilderServiceError) => {
      toast({
        title: t('menuBuilder.errors.unpublishFailed', 'Unpublish failed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteMenuBuilderLayout(branchId),
    
    onSuccess: () => {
      queryClient.setQueryData(menuBuilderKeys.layout(branchId), null);
      queryClient.invalidateQueries({ queryKey: menuBuilderKeys.published(branchId) });
      toast({
        title: t('menuBuilder.actions.deletedSuccessfully', 'Layout deleted'),
        description: t('menuBuilder.actions.deletedSuccessfullyDesc', 'Your custom menu layout has been deleted.'),
      });
    },
    
    onError: (error: MenuBuilderServiceError) => {
      toast({
        title: t('menuBuilder.errors.deleteFailed', 'Delete failed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Derived values - only return layout if there's actual data, otherwise null
  // This prevents creating a new object every render
  const layout = layoutRecord?.layout_data ?? null;
  const layoutVersion = layoutRecord?.layout_version ?? 0;
  const isPublished = layoutRecord?.is_published ?? false;
  const hasLayout = !!layoutRecord;

  return {
    // Data
    layout,
    layoutRecord,
    layoutVersion,
    isPublished,
    hasLayout,
    
    // Loading states
    isLoading,
    error,
    
    // Save
    saveLayout: saveMutation.mutate,
    saveLayoutAsync: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    
    // Publish
    publishLayout: publishMutation.mutate,
    publishLayoutAsync: publishMutation.mutateAsync,
    isPublishing: publishMutation.isPending,
    
    // Unpublish
    unpublishLayout: unpublishMutation.mutate,
    unpublishLayoutAsync: unpublishMutation.mutateAsync,
    isUnpublishing: unpublishMutation.isPending,
    
    // Delete
    deleteLayout: deleteMutation.mutate,
    deleteLayoutAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    
    // Refetch
    refetch,
  };
}

/**
 * Hook for fetching published layout (for public menu)
 * This hook doesn't require authentication
 */
export function usePublishedMenuLayout(branchId: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: menuBuilderKeys.published(branchId),
    queryFn: () => fetchPublishedMenuLayout(branchId),
    enabled: !!branchId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  return {
    layout: data,
    isLoading,
    error,
    hasCustomLayout: !!data && Array.isArray(data.blocks) && data.blocks.length > 0,
  };
}

/**
 * Hook to check if branch has a published custom layout
 */
export function useHasPublishedLayout(branchId: string) {
  const { hasCustomLayout, isLoading } = usePublishedMenuLayout(branchId);
  
  return {
    hasPublishedLayout: hasCustomLayout,
    isLoading,
  };
}
